import { Alert, Box, Flex, Tooltip } from "@chakra-ui/react";
import * as Sentry from "@sentry/browser";
import React, { memo } from "react";
import ReactMarkdown from "react-markdown";

import { IconButton, LoadingIndicator } from "../../../../components";
import CopyOutlineIcon from "../../../../components/Icons/CopyOutlineIcon";
import { copyMarkdownToClipboard } from "../../../../components/MarkdownOutput/copyMarkdownToClipboard";
import { TaskRunQuery, TaskRunStatus } from "../../../graphql";
import { useIsExtension } from "../../../hooks/useAppEnvironmentContext";

type CustomPromptResultProps = {
  taskRun: TaskRunQuery["taskRun"];
  errorMessage?: string;
  loading: boolean;
};

const genericErrorMessage =
  "An unknown error occurred while loading the output.";

const CandidateCustomPromptResult: React.FC<CustomPromptResultProps> = ({
  taskRun,
  errorMessage,
  loading,
}) => {
  if (errorMessage) {
    return (
      <Alert status="error" title="Output Error">
        {genericErrorMessage}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box>
        <LoadingIndicator mt={10} />
      </Box>
    );
  }

  // May occur for one frame while task is starting up
  if (!taskRun) {
    return null;
  }

  if (taskRun.status === TaskRunStatus.Failed) {
    return (
      <Alert status="error" title="Output Error">
        {taskRun.statusMessage || genericErrorMessage}
      </Alert>
    );
  }

  try {
    const parsedOutput = JSON.parse(taskRun.output || "{}");
    if (!parsedOutput.output_text) {
      return (
        <Alert status="info" title="No Output">
          No output found.
        </Alert>
      );
    }

    return (
      <Box fontSize="sm">
        <Flex flexDir="row">
          <Tooltip
            label="Copy to clipboard"
            placement="bottom-start"
            marginLeft="auto"
          >
            <IconButton
              size="sm"
              aria-label="Copy section notes"
              fontSize="sm"
              fontWeight="medium"
              variant="ghost"
              colorScheme="blue"
              ml="auto"
              icon={<CopyOutlineIcon width="16px" height="16px" />}
              onClick={() => copyMarkdownToClipboard(parsedOutput.output_text)}
            />
          </Tooltip>
        </Flex>
        <CandidateCustomPromptMarkdownView
          output={parsedOutput.output_text || "No output found."}
        />
      </Box>
    );
  } catch (e) {
    Sentry.captureException(e);
    return (
      <Alert status="error" title="Output Error">
        The output cannot be displayed.
      </Alert>
    );
  }
};

type CustomPromptOutputProps = {
  output: string;
};

const CandidateCustomPromptMarkdownView: React.FC<CustomPromptOutputProps> =
  memo(({ output }) => {
    const isExtension = useIsExtension();
    return (
      <ReactMarkdown
        components={{
          h2(props) {
            const { node, ...rest } = props;
            return (
              <h2
                style={{
                  margin: "0.5rem 0 0.5rem",
                  fontSize: "var(--chakra-fontSizes-xl)",
                  fontWeight: "600",
                }}
                {...rest}
              />
            );
          },
          h3(props) {
            const { node, ...rest } = props;
            return (
              <h3
                style={{
                  margin: "1rem 0 0.5rem",
                  fontSize: "var(--chakra-fontSizes-lg)",
                  fontWeight: "600",
                }}
                {...rest}
              />
            );
          },
          h4(props) {
            const { node, ...rest } = props;
            return (
              <h4
                style={{
                  margin: "0.5rem 0",
                  fontSize: "var(--chakra-fontSizes-md)",
                  fontWeight: "600",
                }}
                {...rest}
              />
            );
          },
          p(props) {
            const { node, ...rest } = props;
            return <p style={{ margin: "0" }} {...rest} />;
          },
          hr(_) {
            return <span />;
          },
          li(props) {
            const { node, ...rest } = props;
            return <li style={{ margin: "0" }} {...rest} />;
          },
          ul(props) {
            const { node, ...rest } = props;
            return <ul style={{ marginLeft: "1rem" }} {...rest} />;
          },
          ol(props) {
            const { node, ...rest } = props;
            return <ol style={{ marginLeft: "1rem" }} {...rest} />;
          },
          a(props) {
            const { node, ...rest } = props;
            let href = node.properties?.href || "";
            if (typeof href !== "string") {
              return null;
            }

            // Prefix urls with /extension as needed
            if (isExtension && href.startsWith("/interview/")) {
              href = `/extension${href}`;
            }

            return (
              <a
                {...rest}
                target="_blank"
                style={{ color: "var(--chakra-colors-link-default)" }}
                rel="noopener noreferrer"
                href={href}
              />
            );
          },
        }}
      >
        {output}
      </ReactMarkdown>
    );
  });

export default CandidateCustomPromptResult;
